import React, { useEffect, useMemo, useState } from 'react';
import { getPositionCostBasis, getPositionMarketValue, getPositionProfitLoss, getPositionProfitLossPercent, getPositionAnalysis } from './depotValuation';
import {
  buildUiHelpers,
  getPositionSignalInfo,
  sortBySpec,
  getDepotSectionIndexRsl,
} from './sharedUi';
import {
  formatMoney,
  formatDecimalInput,
  getPositionCurrencyLabel,
  TradingViewChart,
} from './sharedFormatters';

const ACTION_BUTTON_STYLE = {
  minWidth: '98px',
  minHeight: '26px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

const REMOVE_BUTTON_STYLE = {
  minWidth: '34px',
  width: '34px',
  minHeight: '30px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  padding: '0',
};

const DETAIL_TOGGLE_BUTTON_STYLE = {
  minWidth: '70px',
  minHeight: '26px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

const CHEVRON_TOGGLE_BUTTON_STYLE = {
  minWidth: '34px',
  width: '34px',
  minHeight: '26px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  padding: '0',
  fontSize: '18px',
  fontWeight: '800',
  lineHeight: '1',
};

const DEPOT_COLUMN_STYLES = {
  rank: { width: '40px' },
  ticker: { width: '62px' },
  name: { width: '140px' },
  industry: { width: '190px' },
  kurs: { width: '100px' },
  rsl: { width: '54px' },
  signal: { width: '74px' },
  costBasis: { width: '100px' },
  marketValue: { width: '100px' },
  pl: { width: '78px' },
  plPercent: { width: '58px' },
  action: { width: '38px' },
};

const SHARED_SIGNAL_COLUMNS = [
  DEPOT_COLUMN_STYLES.rank.width,
  DEPOT_COLUMN_STYLES.ticker.width,
  DEPOT_COLUMN_STYLES.name.width,
  DEPOT_COLUMN_STYLES.industry.width,
  DEPOT_COLUMN_STYLES.kurs.width,
  DEPOT_COLUMN_STYLES.rsl.width,
];
const TOP_COLUMN_STYLES = {
  rank: { width: '54px' },
  ticker: { width: '86px' },
  name: { width: '240px' },
  industry: { width: '280px' },
  kurs: { width: '130px' },
  rsl: { width: '74px' },
  depotAction: { width: '130px' },
  chartAction: { width: '88px' },
};
const TOP_SIGNAL_COLUMNS = [
  TOP_COLUMN_STYLES.rank.width,
  TOP_COLUMN_STYLES.ticker.width,
  TOP_COLUMN_STYLES.name.width,
  TOP_COLUMN_STYLES.industry.width,
  TOP_COLUMN_STYLES.kurs.width,
  TOP_COLUMN_STYLES.rsl.width,
  TOP_COLUMN_STYLES.depotAction.width,
  TOP_COLUMN_STYLES.chartAction.width,
];

function formatPositionMoneyValue(value, currencyLabel) {
  if (!Number.isFinite(value)) return '—';
  return `${formatMoney(Math.round(value)).replace(/,00$/, '')}${currencyLabel ? ` ${currencyLabel}` : ''}`;
}

function formatProfitLoss(value, currencyLabel) {
  if (!Number.isFinite(value)) return '—';
  const roundedValue = Math.round(value);
  const prefix = roundedValue > 0 ? '+' : '';
  return `${prefix}${formatMoney(roundedValue).replace(/,00$/, '')}${currencyLabel ? ` ${currencyLabel}` : ''}`;
}

function formatProfitLossPercent(value) {
  if (!Number.isFinite(value)) return '—';
  const roundedValue = Math.round(value);
  const prefix = roundedValue > 0 ? '+' : '';
  return `${prefix}${roundedValue}%`;
}

function getProfitLossColor(value, COLORS) {
  if (!Number.isFinite(value) || value === 0) return COLORS.muted;
  return value > 0 ? COLORS.green : COLORS.red;
}

function getAnalysisBufferColor(value, COLORS) {
  if (!Number.isFinite(value) || value === 0) return COLORS.muted;
  return value < 0 ? COLORS.green : COLORS.red;
}

function DepotHeaderRow({ th, SortHeader, depotSort, toggleDepotSort }) {
  return (
    <tr>
      <SortHeader label="Rang" sortKey="rang" activeSort={depotSort} onToggle={toggleDepotSort} style={DEPOT_COLUMN_STYLES.rank} />
      <SortHeader label="Ticker" sortKey="ticker" activeSort={depotSort} onToggle={toggleDepotSort} style={DEPOT_COLUMN_STYLES.ticker} />
      <SortHeader label="Name" sortKey="name" activeSort={depotSort} onToggle={toggleDepotSort} style={DEPOT_COLUMN_STYLES.name} />
      <SortHeader label="Branche" sortKey="industry" activeSort={depotSort} onToggle={toggleDepotSort} style={DEPOT_COLUMN_STYLES.industry} />
      <SortHeader label="Kurs" sortKey="kurs" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={DEPOT_COLUMN_STYLES.kurs} />
      <SortHeader label="RSL" sortKey="rsl_titel" activeSort={depotSort} onToggle={toggleDepotSort} style={DEPOT_COLUMN_STYLES.rsl} />
      <SortHeader label="Signal" sortKey="signal" activeSort={depotSort} onToggle={toggleDepotSort} style={DEPOT_COLUMN_STYLES.signal} />
      <SortHeader label="Kostenbasis" sortKey="cost_basis" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={DEPOT_COLUMN_STYLES.costBasis} />
      <SortHeader label="Marktwert" sortKey="market_value" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={DEPOT_COLUMN_STYLES.marketValue} />
      <SortHeader label="P/L" sortKey="pl" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={DEPOT_COLUMN_STYLES.pl} />
      <SortHeader label="P/L %" sortKey="pl_percent" activeSort={depotSort} onToggle={toggleDepotSort} align="right" style={DEPOT_COLUMN_STYLES.plPercent} />
      <th style={{ ...th, ...DEPOT_COLUMN_STYLES.action }} />
    </tr>
  );
}

export function PositionDetailRow({
  position,
  colSpan,
  onSave,
  onClosePosition,
  onAddTranche,
  onDeleteTranche,
  themeMode,
  COLORS,
  th,
  td,
  inputStyle,
}) {
  const { Btn } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);
  const [reinDatum, setReinDatum] = useState(position?.rein_datum || '');
  const [menge, setMenge] = useState(formatDecimalInput(position?.menge));
  const [einstandskurs, setEinstandskurs] = useState(formatDecimalInput(position?.einstandskurs));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [tranchenDrafts, setTranchenDrafts] = useState({});
  const [newTrancheDate, setNewTrancheDate] = useState(position?.rein_datum || '');
  const [newTrancheMenge, setNewTrancheMenge] = useState('');
  const [newTrancheEinstand, setNewTrancheEinstand] = useState('');
  const [buySaving, setBuySaving] = useState(false);
  const [deletingTrancheId, setDeletingTrancheId] = useState(null);

  useEffect(() => {
    setReinDatum(position?.rein_datum || '');
    setMenge(formatDecimalInput(position?.menge));
    setEinstandskurs(formatDecimalInput(position?.einstandskurs));
    const nextDrafts = {};
    (Array.isArray(position?.tranchen) ? position.tranchen : []).forEach(tranche => {
      nextDrafts[tranche.id] = {
        rein_datum: tranche.rein_datum || '',
        menge: formatDecimalInput(tranche.menge),
        einstandskurs: formatDecimalInput(tranche.einstandskurs),
      };
    });
    setTranchenDrafts(nextDrafts);
    setNewTrancheDate(position?.rein_datum || '');
    setNewTrancheMenge('');
    setNewTrancheEinstand('');
    setErr('');
    setMsg('');
  }, [position?.id, position?.rein_datum, position?.menge, position?.einstandskurs, position?.lot_count, position?.tranchen]);

  const parsedMenge = Number.parseFloat(String(menge).replace(',', '.'));
  const parsedEinstand = Number.parseFloat(String(einstandskurs).replace(',', '.'));
  const summe = Number.isFinite(parsedMenge) && Number.isFinite(parsedEinstand)
    ? parsedMenge * parsedEinstand
    : null;

  async function speichern() {
    setErr('');
    setMsg('');
    const hasMenge = Number.isFinite(parsedMenge);
    const hasEinstand = Number.isFinite(parsedEinstand);
    if (hasMenge !== hasEinstand) {
      setErr('Bitte Menge und Einstandskurs gemeinsam ausfüllen oder gemeinsam leeren');
      return;
    }
    if (hasMenge && parsedMenge <= 0) {
      setErr('Bitte eine gültige Menge größer als 0 angeben');
      return;
    }
    if (hasEinstand && parsedEinstand <= 0) {
      setErr('Bitte einen gültigen Einstandskurs größer als 0 angeben');
      return;
    }

    setSaving(true);
    try {
      await onSave(position.id, {
        rein_datum: reinDatum || null,
        menge: hasMenge ? parsedMenge : null,
        einstandskurs: hasEinstand ? parsedEinstand : null,
      });
      setMsg('Gespeichert');
    } catch (error) {
      setErr(error?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  function zuruecksetzen() {
    setMenge('');
    setEinstandskurs('');
    setErr('');
    setMsg('');
  }

  function updateTrancheDraft(trancheId, field, value) {
    setTranchenDrafts(current => ({
      ...current,
      [trancheId]: {
        ...(current[trancheId] || {}),
        [field]: value,
      },
    }));
  }

  async function speichereTranche(trancheId) {
    const draft = tranchenDrafts[trancheId] || {};
    const parsedTrancheMenge = Number.parseFloat(String(draft.menge ?? '').replace(',', '.'));
    const parsedTrancheEinstand = Number.parseFloat(String(draft.einstandskurs ?? '').replace(',', '.'));
    const hasMenge = Number.isFinite(parsedTrancheMenge);
    const hasEinstand = Number.isFinite(parsedTrancheEinstand);
    if (hasMenge !== hasEinstand) {
      setErr('Bitte Menge und Einstandskurs je Tranche gemeinsam setzen oder gemeinsam leeren');
      return;
    }
    if (hasMenge && parsedTrancheMenge <= 0) {
      setErr('Bitte pro Tranche eine Menge größer als 0 angeben');
      return;
    }
    if (hasEinstand && parsedTrancheEinstand <= 0) {
      setErr('Bitte pro Tranche einen Einstandskurs größer als 0 angeben');
      return;
    }
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      await onSave(trancheId, {
        rein_datum: draft.rein_datum || null,
        menge: hasMenge ? parsedTrancheMenge : null,
        einstandskurs: hasEinstand ? parsedTrancheEinstand : null,
      });
      setMsg('Tranche gespeichert');
    } catch (error) {
      setErr(error?.message || 'Tranche konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  }

  async function trancheHinzufuegen() {
    if (typeof onAddTranche !== 'function') return;
    const parsedBuyMenge = Number.parseFloat(String(newTrancheMenge).replace(',', '.'));
    const parsedBuyEinstand = Number.parseFloat(String(newTrancheEinstand).replace(',', '.'));
    if (!Number.isFinite(parsedBuyMenge) || parsedBuyMenge <= 0) {
      setErr('Bitte pro Tranche eine Menge größer als 0 angeben');
      return;
    }
    if (!Number.isFinite(parsedBuyEinstand) || parsedBuyEinstand <= 0) {
      setErr('Bitte einen gültigen Nachkauf-Einstand größer als 0 angeben');
      return;
    }
    setBuySaving(true);
    setErr('');
    setMsg('');
    try {
      await onAddTranche(position.id, {
        rein_datum: newTrancheDate || null,
        menge: parsedBuyMenge,
        einstandskurs: parsedBuyEinstand,
      });
      setMsg('Nachkauf hinzugefügt');
      setNewTrancheMenge('');
      setNewTrancheEinstand('');
    } catch (error) {
      setErr(error?.message || 'Nachkauf konnte nicht gespeichert werden');
    } finally {
      setBuySaving(false);
    }
  }

  async function loescheTranche(trancheId) {
    const trancheCount = Array.isArray(position?.tranchen) ? position.tranchen.length : 0;
    if (trancheCount <= 1 && typeof onClosePosition === 'function') {
      onClosePosition(position);
      return;
    }
    if (typeof onDeleteTranche !== 'function') return;
    setDeletingTrancheId(trancheId);
    setErr('');
    setMsg('');
    try {
      await onDeleteTranche(position.id, trancheId);
      setMsg('Tranche gelöscht');
    } catch (error) {
      setErr(error?.message || 'Tranche konnte nicht gelöscht werden');
    } finally {
      setDeletingTrancheId(null);
    }
  }

  const hasTranchen = Array.isArray(position?.tranchen) && position.tranchen.length > 0;

  return (
    <tr>
      <td colSpan={colSpan} style={{ ...td, padding: '14px 12px', background: themeMode === 'light' ? '#f7fbfd' : '#ffffff04' }}>
        <div style={{ display: 'grid', gap: '12px' }}>
          {hasTranchen ? (
            <>
              <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.text }}>Tranchen (FIFO: älteste zuerst)</div>
              <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: '10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#ffffff06' }}>
                      <th style={{ ...th, width: '140px' }}>Kaufdatum</th>
                      <th style={{ ...th, width: '130px' }}>Menge</th>
                      <th style={{ ...th, width: '140px' }}>Einstand</th>
                      <th style={{ ...th, width: '140px', textAlign: 'right' }}>Summe</th>
                      <th style={{ ...th, width: '170px' }}>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {position.tranchen.map(tranche => {
                      const trancheCount = Array.isArray(position?.tranchen) ? position.tranchen.length : 0;
                      const isLastRemainingTranche = trancheCount <= 1;
                      const draft = tranchenDrafts[tranche.id] || {};
                      const parsedQty = Number.parseFloat(String(draft.menge ?? '').replace(',', '.'));
                      const parsedEntry = Number.parseFloat(String(draft.einstandskurs ?? '').replace(',', '.'));
                      const trancheSum = Number.isFinite(parsedQty) && Number.isFinite(parsedEntry) ? parsedQty * parsedEntry : null;
                      return (
                        <tr key={tranche.id}>
                          <td style={td}>
                            <input
                              type="date"
                              autoComplete="off"
                              value={draft.rein_datum || ''}
                              onChange={event => updateTrancheDraft(tranche.id, 'rein_datum', event.target.value)}
                              style={{ ...inputStyle, marginBottom: 0 }}
                            />
                          </td>
                          <td style={td}>
                            <input
                              type="number"
                              min="0"
                              step="0.000001"
                              value={draft.menge ?? ''}
                              onChange={event => updateTrancheDraft(tranche.id, 'menge', event.target.value)}
                              style={{ ...inputStyle, marginBottom: 0 }}
                            />
                          </td>
                          <td style={td}>
                            <input
                              type="number"
                              min="0"
                              step="0.000001"
                              value={draft.einstandskurs ?? ''}
                              onChange={event => updateTrancheDraft(tranche.id, 'einstandskurs', event.target.value)}
                              style={{ ...inputStyle, marginBottom: 0 }}
                            />
                          </td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            {trancheSum !== null ? `${formatMoney(trancheSum)}${getPositionCurrencyLabel(position.index) ? ` ${getPositionCurrencyLabel(position.index)}` : ''}` : '—'}
                          </td>
                          <td style={td}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <Btn onClick={() => speichereTranche(tranche.id)} color="green" size="sm" disabled={saving || deletingTrancheId === tranche.id}>
                                {saving ? 'Speichert...' : 'Speichern'}
                              </Btn>
                              <Btn
                                onClick={() => loescheTranche(tranche.id)}
                                color="red"
                                size="sm"
                                title={isLastRemainingTranche ? 'Position schließen / verkaufen' : 'Tranche löschen'}
                                aria-label={isLastRemainingTranche ? 'Position schließen / verkaufen' : 'Tranche löschen'}
                                style={REMOVE_BUTTON_STYLE}
                                disabled={saving || deletingTrancheId === tranche.id}
                              >
                                X
                              </Btn>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.text }}>Nachkauf hinzufügen</div>
              <div style={{ display: 'grid', gridTemplateColumns: '180px 140px 160px auto', gap: '10px', alignItems: 'end' }}>
                <div>
                  <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Kaufdatum</div>
                  <input
                    type="date"
                    value={newTrancheDate}
                    onChange={event => setNewTrancheDate(event.target.value)}
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Menge</div>
                  <input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={newTrancheMenge}
                    onChange={event => setNewTrancheMenge(event.target.value)}
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Einstandskurs</div>
                  <input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={newTrancheEinstand}
                    onChange={event => setNewTrancheEinstand(event.target.value)}
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Btn onClick={trancheHinzufuegen} color="green" size="sm" disabled={buySaving}>
                    {buySaving ? 'Speichert...' : '+ Nachkauf'}
                  </Btn>
                </div>
              </div>
            </>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '180px 110px 140px 160px 180px auto',
              gap: '10px',
              alignItems: 'end',
            }}>
              <div>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Kaufdatum</div>
                <div style={{ position: 'relative' }}>
                  <input
                    type="date"
                    autoComplete="off"
                    value={reinDatum}
                    onChange={e => setReinDatum(e.target.value)}
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Tage im Depot</div>
                <div style={{ ...inputStyle, marginBottom: 0, display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                  {Number.isFinite(position?.tage) ? `${position.tage}d` : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Menge</div>
                <input type="number" autoComplete="off" min="0" step="0.000001" value={menge} onChange={e => setMenge(e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Einstandskurs</div>
                <input type="number" autoComplete="off" min="0" step="0.000001" value={einstandskurs} onChange={e => setEinstandskurs(e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Summe</div>
                <div style={{ ...inputStyle, marginBottom: 0, display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                  {summe !== null ? `${formatMoney(summe)}${getPositionCurrencyLabel(position.index) ? ` ${getPositionCurrencyLabel(position.index)}` : ''}` : '—'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Btn onClick={speichern} color="green" size="sm">{saving ? 'Speichert...' : 'Speichern'}</Btn>
                <Btn onClick={zuruecksetzen} color="ghost" size="sm" title="Felder leeren" aria-label="Felder leeren">×</Btn>
              </div>
            </div>
          )}
          {err && <div style={{ color: COLORS.red, fontSize: '13px' }}>⚠ {err}</div>}
          {msg && <div style={{ color: COLORS.green, fontSize: '13px' }}>✓ {msg}</div>}
        </div>
      </td>
    </tr>
  );
}

export function ScreenerTabelle({
  titel,
  marktLabel,
  marktKey,
  currency,
  customWatchlistId = null,
  depot,
  onKaufen,
  onEntfernen,
  onSpeichernPosition,
  onAddTranche,
  onDeleteTranche,
  onCopyTickers,
  chartsEnabled,
  themeMode,
  COLORS,
  th,
  td,
  inputStyle,
  showLoadMoreTopSignals = false,
  hasMoreTopRows = false,
  onLoadMoreTopRows,
  onRemoveSignal,
  showDepotSection = true,
}) {
  const { Badge, Btn, SortHeader } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);
  const [topSort, setTopSort] = useState({ key: 'rang', direction: 'asc' });
  const [depotSort, setDepotSort] = useState({ key: 'rang', direction: 'asc' });
  const [openTopChartKey, setOpenTopChartKey] = useState('');
  const [openDepotDetailKey, setOpenDepotDetailKey] = useState('');
  const rows = useMemo(() => (Array.isArray(titel) ? titel : []), [titel]);
  const topMetaByTicker = useMemo(
    () => Object.fromEntries(
      rows
        .filter(item => item?.ticker)
        .map(item => [String(item.ticker).trim().toUpperCase(), item])
    ),
    [rows]
  );
  const currencyCode = marktKey === 'CUSTOM'
    ? (String(currency || '').trim().toUpperCase() || 'USD')
    : (marktKey === 'HDAX' || marktKey === 'STOXX600' ? 'EUR' : 'USD');
  const indexRslValue = Number.isFinite(rows?.[0]?.rsl_index) ? rows[0].rsl_index : null;

  const sortedRows = sortBySpec(rows, topSort, (item, key) => {
    if (key === 'rang') return Number.isFinite(item?.rang) ? item.rang : null;
    if (key === 'ticker') return item?.ticker;
    if (key === 'name') return item?.name;
    if (key === 'industry') return item?.industry;
    if (key === 'kurs') return Number.isFinite(item?.kurs) ? item.kurs : null;
    if (key === 'rsl_titel') return Number.isFinite(item?.rsl_titel) ? item.rsl_titel : null;
    if (key === 'depot') {
      return !!depot.find(p => (
        p.symbol === item.ticker
        && p.index === marktKey
        && (marktKey !== 'CUSTOM' || p.custom_watchlist_id === customWatchlistId)
      ));
    }
    return item?.[key];
  });

  const depotRows = sortBySpec(
    (Array.isArray(depot) ? depot : [])
      .filter(p => (
        p.index === marktKey
        && (marktKey !== 'CUSTOM' || p.custom_watchlist_id === customWatchlistId)
      ))
      .map(position => {
        const symbolKey = String(position?.symbol || '').trim().toUpperCase();
        const topMeta = topMetaByTicker[symbolKey] || {};
        const name = String(position?.name || '').trim();
        const industry = String(position?.industry || '').trim();
        return {
          ...position,
          name: name || String(topMeta?.name || '').trim(),
          industry: industry || String(topMeta?.industry || '').trim(),
          tradingview_symbol: String(position?.tradingview_symbol || topMeta?.tradingview_symbol || '').trim(),
        };
      }),
    depotSort,
    (item, key) => {
      const { signal } = getPositionSignalInfo(item);
      if (key === 'rang') return Number.isFinite(item?.rang) ? item.rang : null;
      if (key === 'ticker') return item?.symbol;
      if (key === 'name') return item?.name;
      if (key === 'kaufdatum') return item?.rein_datum;
      if (key === 'tage') return Number.isFinite(item?.tage) ? item.tage : null;
      if (key === 'kurs') return Number.isFinite(item?.kurs) ? item.kurs : null;
      if (key === 'rsl_titel') return Number.isFinite(item?.rsl_titel) ? item.rsl_titel : null;
      if (key === 'signal') return signal;
      if (key === 'cost_basis') return getPositionCostBasis(item);
      if (key === 'market_value') return getPositionMarketValue(item);
      if (key === 'pl') return getPositionProfitLoss(item);
      if (key === 'pl_percent') return getPositionProfitLossPercent(item);
      return item?.[key];
    }
  );
  const depotProfitLossTotal = depotRows.reduce((sum, item) => {
    const pl = getPositionProfitLoss(item);
    return Number.isFinite(pl) ? sum + pl : sum;
  }, 0);
  const depotCostBasisTotal = depotRows.reduce((sum, item) => {
    const costBasis = Number(item?.summe);
    return Number.isFinite(costBasis) ? sum + costBasis : sum;
  }, 0);
  const depotProfitLossPercentTotal = depotCostBasisTotal > 0
    ? (depotProfitLossTotal / depotCostBasisTotal) * 100
    : null;

  const toggleTopSort = key => setTopSort(current => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));
  const toggleDepotSort = key => setDepotSort(current => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));

  return (
    <div style={{ marginBottom: '28px' }}>
      <h3
        onClick={() => onCopyTickers(rows.map(t => ({ symbol: t.ticker })), `${marktLabel} – Top Signale`)}
        style={{ margin: '0 0 14px', fontSize: '15px', color: COLORS.text, cursor: 'pointer' }}
      >
        {marktLabel} – Top Signale
      </h3>
      <div style={{ marginBottom: '10px', fontSize: '13px', color: COLORS.muted }}>
        <span style={{ color: COLORS.text, fontWeight: '800' }}>RSL Index: {indexRslValue !== null ? indexRslValue.toFixed(2) : '–'}</span>
      </div>
      <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${COLORS.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {TOP_SIGNAL_COLUMNS.map((width, idx) => <col key={`top-signal-${idx}`} style={{ width }} />)}
            {onRemoveSignal && <col style={{ width: '46px' }} />}
          </colgroup>
          <thead style={{ background: '#ffffff05' }}>
            <tr>
              <SortHeader label="Rang" sortKey="rang" activeSort={topSort} onToggle={toggleTopSort} style={TOP_COLUMN_STYLES.rank} />
              <SortHeader label="Ticker" sortKey="ticker" activeSort={topSort} onToggle={toggleTopSort} style={TOP_COLUMN_STYLES.ticker} />
              <SortHeader label="Name" sortKey="name" activeSort={topSort} onToggle={toggleTopSort} style={TOP_COLUMN_STYLES.name} />
              <SortHeader label="Branche" sortKey="industry" activeSort={topSort} onToggle={toggleTopSort} style={TOP_COLUMN_STYLES.industry} />
              <SortHeader label="Kurs" sortKey="kurs" activeSort={topSort} onToggle={toggleTopSort} align="right" style={TOP_COLUMN_STYLES.kurs} />
              <SortHeader label="RSL" sortKey="rsl_titel" activeSort={topSort} onToggle={toggleTopSort} style={TOP_COLUMN_STYLES.rsl} />
              <th style={{ ...th, ...TOP_COLUMN_STYLES.depotAction }} />
              <th style={{ ...th, ...TOP_COLUMN_STYLES.chartAction }} />
              {onRemoveSignal && <th style={{ ...th, width: '46px' }} />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={onRemoveSignal ? 9 : 8} style={{ ...td, color: COLORS.muted, textAlign: 'center' }}>
                  Aktuell keine Top-Signale für {marktLabel}.
                </td>
              </tr>
            )}
            {sortedRows.map((t, i) => {
              const imDepot = depot.find(p => (
                p.symbol === t.ticker
                && p.index === marktKey
                && (marktKey !== 'CUSTOM' || p.custom_watchlist_id === customWatchlistId)
              ));
              const rankColor = !t.rang ? 'indigo' : t.rang <= 5 ? 'green' : t.rang <= 7 ? 'yellow' : 'indigo';
              return (
                <React.Fragment key={t.ticker}>
                  <tr style={{ background: i % 2 === 0 ? 'transparent' : '#ffffff03' }}>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.rank }}><Badge color={rankColor}>#{t.rang}</Badge></td>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.ticker, fontWeight: '700', color: COLORS.cyan }}>{t.ticker}</td>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.name, maxWidth: '230px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</td>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.industry, color: COLORS.muted, fontSize: '12px', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.industry}</td>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.kurs, textAlign: 'right' }}>{Number.isFinite(t.kurs) ? `${t.kurs.toFixed(2)} ${currencyCode}` : `– ${currencyCode}`}</td>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.rsl }}>
                      <RslValue
                        Badge={Badge}
                        value={t.rsl_titel}
                        color={
                          typeof t.rsl_staerker === 'boolean'
                            ? (t.rsl_staerker ? 'green' : 'red')
                            : (Number.isFinite(t.rsl_titel) && t.rsl_titel >= 1 ? 'green' : 'red')
                        }
                        warning={Boolean(t.data_quality_warning)}
                      />
                    </td>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.depotAction, textAlign: 'right' }}>
                      {imDepot ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: ACTION_BUTTON_STYLE.minWidth,
                          minHeight: ACTION_BUTTON_STYLE.minHeight,
                          background: '#164e6344',
                          color: COLORS.cyan,
                          border: `1px solid ${COLORS.cyan}44`,
                          borderRadius: '10px',
                          padding: '4px 9px',
                          fontSize: '11px',
                          fontWeight: '700',
                          boxSizing: 'border-box',
                        }}>
                          ✓ Im Depot
                        </span>
                      ) : (
                        <Btn onClick={() => onKaufen(t.ticker, marktKey)} color="green" size="sm" style={ACTION_BUTTON_STYLE}>
                          + Hinzufügen
                        </Btn>
                      )}
                    </td>
                    <td style={{ ...td, ...TOP_COLUMN_STYLES.chartAction, textAlign: 'right' }}>
                      {chartsEnabled && (
                        <Btn onClick={() => setOpenTopChartKey(current => (current === t.ticker ? '' : t.ticker))} color="ghost" size="sm" style={DETAIL_TOGGLE_BUTTON_STYLE}>
                          {openTopChartKey === t.ticker ? 'Ausblenden' : 'Chart'}
                        </Btn>
                      )}
                    </td>
                    {onRemoveSignal && (
                      <td style={{ ...td, width: '46px', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => onRemoveSignal(t)}
                          title={`${t.ticker} aus der Watchlist entfernen`}
                          aria-label={`${t.ticker} aus der Watchlist entfernen`}
                          style={{
                            ...REMOVE_BUTTON_STYLE,
                            border: `1px solid ${COLORS.red}55`,
                            borderRadius: '8px',
                            background: `${COLORS.red}16`,
                            color: COLORS.red,
                            cursor: 'pointer',
                          }}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                  {chartsEnabled && openTopChartKey === t.ticker && (
                    <tr>
                      <td colSpan={onRemoveSignal ? 9 : 8} style={{ ...td, padding: '12px', background: '#ffffff04' }}>
                        <TradingViewChart
                          symbol={t.ticker}
                          marketKey={marktKey}
                          tradingViewSymbol={t.tradingview_symbol}
                          themeMode={themeMode}
                          COLORS={COLORS}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {showLoadMoreTopSignals && (
        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
          <Btn onClick={onLoadMoreTopRows} color="ghost" size="sm">
            + 10 weitere laden
          </Btn>
        </div>
      )}
      {showDepotSection && <div style={{ marginTop: '12px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.text, marginBottom: '8px' }}>
          {marktLabel} Titel aus dem Depot
        </div>
        <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${COLORS.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              {SHARED_SIGNAL_COLUMNS.map((width, idx) => <col key={`depot-shared-${idx}`} style={{ width }} />)}
              <col style={{ width: DEPOT_COLUMN_STYLES.signal.width }} />
              <col style={{ width: DEPOT_COLUMN_STYLES.costBasis.width }} />
              <col style={{ width: DEPOT_COLUMN_STYLES.marketValue.width }} />
              <col style={{ width: DEPOT_COLUMN_STYLES.pl.width }} />
              <col style={{ width: DEPOT_COLUMN_STYLES.plPercent.width }} />
              <col style={{ width: DEPOT_COLUMN_STYLES.action.width }} />
            </colgroup>
            <tbody>
              {depotRows.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ ...td, color: COLORS.muted, textAlign: 'center' }}>
                    Aktuell keine {marktLabel}-Titel im Depot.
                  </td>
                </tr>
              )}
              {depotRows.length > 0 && (
                <tr>
                  <td colSpan={12} style={{ ...td, color: COLORS.muted, fontSize: '12px', background: '#ffffff03' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{ color: COLORS.text, fontWeight: '800' }}>RSL Index: {getDepotSectionIndexRsl(depotRows) !== null ? getDepotSectionIndexRsl(depotRows).toFixed(2) : '–'}</span>
                      <span style={{ color: getProfitLossColor(depotProfitLossTotal, COLORS), fontWeight: '800' }}>
                        Buch-P/L: {formatProfitLoss(depotProfitLossTotal, currencyCode)} {depotProfitLossPercentTotal !== null ? `(${formatProfitLossPercent(depotProfitLossPercentTotal)})` : ''}
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {depotRows.length > 0 && (
                <DepotHeaderRow th={th} SortHeader={SortHeader} depotSort={depotSort} toggleDepotSort={toggleDepotSort} />
              )}
              {depotRows.map((p, i) => {
                const { signal, sigColor, rslOk } = getPositionSignalInfo(p);
                const rankColor = !p.rang ? 'indigo' : p.rang <= 5 ? 'green' : p.rang <= 7 ? 'yellow' : 'indigo';
                return (
                  <React.Fragment key={p.id}>
                    <tr style={{ background: i % 2 === 0 ? 'transparent' : '#ffffff03' }}>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.rank }}>
                        {p.rang ? <Badge color={rankColor}>#{p.rang}</Badge> : <span style={{ color: COLORS.muted }}>-</span>}
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.ticker, fontWeight: '700', color: COLORS.cyan }}>{p.symbol}</td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.name, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || '—'}</td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.industry, color: COLORS.muted, fontSize: '12px' }}>{p.industry || '—'}</td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.kurs, textAlign: 'right' }}>
                        {Number.isFinite(p.kurs) ? `${formatMoney(p.kurs)} ${currencyCode}` : `— ${currencyCode}`}
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.rsl }}>
                        <RslValue
                          Badge={Badge}
                          value={p.rsl_titel}
                          color={rslOk === null ? 'cyan' : (rslOk ? 'green' : 'red')}
                          warning={Boolean(p.data_quality_warning)}
                        />
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.signal }}>
                        <Badge color={sigColor}>{signal}</Badge>
                        {p.is_index_member === false && <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '4px' }}>Index verlassen</div>}
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.costBasis, textAlign: 'right', color: COLORS.text }}>
                        {formatPositionMoneyValue(getPositionCostBasis(p), currencyCode)}
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.marketValue, textAlign: 'right', color: COLORS.text }}>
                        {formatPositionMoneyValue(getPositionMarketValue(p), currencyCode)}
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.pl, textAlign: 'right', color: getProfitLossColor(getPositionProfitLoss(p), COLORS), fontWeight: '700' }}>
                        {formatProfitLoss(getPositionProfitLoss(p), currencyCode)}
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.plPercent, textAlign: 'right', color: getProfitLossColor(getPositionProfitLossPercent(p), COLORS), fontWeight: '700' }}>
                        {formatProfitLossPercent(getPositionProfitLossPercent(p))}
                      </td>
                      <td style={{ ...td, ...DEPOT_COLUMN_STYLES.action }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                          <Btn
                            onClick={() => setOpenDepotDetailKey(current => (current === String(p.id) ? '' : String(p.id)))}
                            color="ghost"
                            size="sm"
                            title={openDepotDetailKey === String(p.id) ? 'Details ausblenden' : 'Details anzeigen'}
                            style={CHEVRON_TOGGLE_BUTTON_STYLE}
                          >
                            {openDepotDetailKey === String(p.id) ? '▾' : '▸'}
                          </Btn>
                        </div>
                      </td>
                    </tr>
                    {openDepotDetailKey === String(p.id) && (
                      <PositionDetailRow
                        position={p}
                        colSpan={12}
                        onSave={onSpeichernPosition}
                        onClosePosition={onEntfernen}
                        onAddTranche={onAddTranche}
                        onDeleteTranche={onDeleteTranche}
                        themeMode={themeMode}
                        COLORS={COLORS}
                        th={th}
                        td={td}
                        inputStyle={inputStyle}
                      />
                    )}
                    {chartsEnabled && openDepotDetailKey === String(p.id) && (
                      <tr>
                        <td colSpan={12} style={{ ...td, padding: '12px', background: '#ffffff04' }}>
                          <TradingViewChart
                            symbol={p.symbol}
                            marketKey={marktKey}
                            tradingViewSymbol={p.tradingview_symbol}
                            themeMode={themeMode}
                            COLORS={COLORS}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  );
}

export function DepotRows({
  positionen,
  analysisMode = false,
  onEntfernen,
  onSpeichernPosition,
  onAddTranche,
  onDeleteTranche,
  chartsEnabled,
  themeMode,
  COLORS,
  th,
  td,
  inputStyle,
}) {
  const { Badge, Btn } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);
  const [openDetailKey, setOpenDetailKey] = useState('');
  const marketIndex = positionen[0]?.index;
  const currencyCode = marketIndex === 'HDAX' || marketIndex === 'STOXX600' ? 'EUR' : marketIndex === 'SP500' || marketIndex === 'NASDAQ100' ? 'USD' : '';

  return positionen.map((p, i) => {
    const { signal, sigColor, rslOk } = getPositionSignalInfo(p);
    const rankColor = !p.rang ? 'indigo' : p.rang <= 5 ? 'green' : p.rang <= 7 ? 'yellow' : 'indigo';
    const analysis = getPositionAnalysis(p);

    return (
      <React.Fragment key={p.id}>
        <tr style={{ background: i % 2 === 0 ? 'transparent' : '#ffffff03' }}>
          <td style={{ ...td, ...DEPOT_COLUMN_STYLES.rank }}>
            {p.rang ? <Badge color={rankColor}>#{p.rang}</Badge> : <span style={{ color: COLORS.muted }}>-</span>}
          </td>
          <td style={{ ...td, ...DEPOT_COLUMN_STYLES.ticker, fontWeight: '700', color: COLORS.cyan }}>{p.symbol}</td>
          <td style={{ ...td, ...DEPOT_COLUMN_STYLES.name, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || '—'}</td>
          <td style={{ ...td, ...DEPOT_COLUMN_STYLES.industry, color: COLORS.muted, fontSize: '12px', maxWidth: '190px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.industry || '—'}</td>
          {!analysisMode && (
            <td style={{ ...td, ...DEPOT_COLUMN_STYLES.kurs, textAlign: 'right' }}>
              {Number.isFinite(p.kurs) ? `${formatMoney(p.kurs)} ${currencyCode}` : currencyCode ? `— ${currencyCode}` : '—'}
            </td>
          )}
          <td style={{ ...td, ...DEPOT_COLUMN_STYLES.rsl }}>
            <RslValue
              Badge={Badge}
              value={p.rsl_titel}
              color={rslOk === null ? 'cyan' : (rslOk ? 'green' : 'red')}
              warning={Boolean(p.data_quality_warning)}
            />
          </td>
          {analysisMode ? (
            <>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.pl, textAlign: 'right', color: getProfitLossColor(analysis.exitPl, COLORS), fontWeight: '700' }}>
                {Number.isFinite(analysis.exitPl) ? `${formatProfitLoss(analysis.exitPl, currencyCode)}${Number.isFinite(analysis.exitPlPercent) ? ` (${formatProfitLossPercent(analysis.exitPlPercent)})` : ''}` : '—'}
              </td>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.pl, textAlign: 'right', color: getAnalysisBufferColor(analysis.unsafeGain, COLORS), fontWeight: '700' }}>
                {Number.isFinite(analysis.unsafeGain) ? formatProfitLoss(analysis.unsafeGain, currencyCode) : '—'}
              </td>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.plPercent, textAlign: 'right', color: getAnalysisBufferColor(analysis.distancePercent, COLORS), fontWeight: '700' }}>
                {Number.isFinite(analysis.distancePercent) ? formatProfitLossPercent(analysis.distancePercent) : '—'}
              </td>
            </>
          ) : (
            <>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.signal }}>
                <Badge color={sigColor}>{signal}</Badge>
                {p.is_index_member === false && <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '4px' }}>Index verlassen</div>}
              </td>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.costBasis, textAlign: 'right', color: COLORS.text }}>
                {formatPositionMoneyValue(getPositionCostBasis(p), currencyCode)}
              </td>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.marketValue, textAlign: 'right', color: COLORS.text }}>
                {formatPositionMoneyValue(getPositionMarketValue(p), currencyCode)}
              </td>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.pl, textAlign: 'right', color: getProfitLossColor(getPositionProfitLoss(p), COLORS), fontWeight: '700' }}>
                {formatProfitLoss(getPositionProfitLoss(p), currencyCode)}
              </td>
              <td style={{ ...td, ...DEPOT_COLUMN_STYLES.plPercent, textAlign: 'right', color: getProfitLossColor(getPositionProfitLossPercent(p), COLORS), fontWeight: '700' }}>
                {formatProfitLossPercent(getPositionProfitLossPercent(p))}
              </td>
            </>
          )}
          <td style={{ ...td, ...DEPOT_COLUMN_STYLES.action }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
              <Btn
                onClick={() => setOpenDetailKey(current => (current === String(p.id) ? '' : String(p.id)))}
                color="ghost"
                size="sm"
                title={openDetailKey === String(p.id) ? 'Details ausblenden' : 'Details anzeigen'}
                style={CHEVRON_TOGGLE_BUTTON_STYLE}
              >
                {openDetailKey === String(p.id) ? '▾' : '▸'}
              </Btn>
            </div>
          </td>
        </tr>
        {openDetailKey === String(p.id) && (
          <PositionDetailRow
            position={p}
            colSpan={analysisMode ? 9 : 12}
            onSave={onSpeichernPosition}
            onClosePosition={onEntfernen}
            onAddTranche={onAddTranche}
            onDeleteTranche={onDeleteTranche}
            themeMode={themeMode}
            COLORS={COLORS}
            th={th}
            td={td}
            inputStyle={inputStyle}
          />
        )}
        {chartsEnabled && openDetailKey === String(p.id) && (
          <tr>
            <td colSpan={analysisMode ? 9 : 12} style={{ ...td, padding: '12px', background: '#ffffff04' }}>
              <TradingViewChart
                symbol={p.symbol}
                marketKey={p.index}
                tradingViewSymbol={p.tradingview_symbol}
                themeMode={themeMode}
                COLORS={COLORS}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  });
}

const DATA_QUALITY_WARNING_TOOLTIP = 'Möglicher Split oder Datenfehler. Bitte den Kursverlauf vor einer Entscheidung prüfen.';

function RslValue({ Badge, value, color, warning = false, warningReason = '' }) {
  if (!Number.isFinite(value)) {
    return <span style={{ color: '#8fa3ad' }}>–</span>;
  }

  const tooltip = warningReason || DATA_QUALITY_WARNING_TOOLTIP;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <Badge color={color}>{value.toFixed(2)}</Badge>
      {warning ? (
        <span
          aria-label={tooltip}
          title={tooltip}
          style={{
            color: '#f59e0b',
            filter: 'drop-shadow(0 0 5px rgba(245, 158, 11, 0.65))',
            cursor: 'help',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            fontSize: 20,
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          ⚠️
        </span>
      ) : null}
    </span>
  );
}
